import { useState, useEffect, useRef, useCallback } from 'react'
import classNames from 'classnames'
import { useDispatch } from 'react-redux'
import { Dialog, Tooltip, Notification, toast } from 'components/ui'
import { useTranslation } from 'react-i18next'
import {
    HiOutlineDownload,
    HiOutlineExternalLink,
    HiOutlineX,
    HiOutlineCheck,
    HiOutlineZoomIn,
    HiOutlineZoomOut,
    HiOutlineArrowsExpand,
} from 'react-icons/hi'
import { LuRotateCcw, LuRotateCw } from 'react-icons/lu'
import { apiRotateImage } from 'services/TicketsService'
import { statusResponse } from 'constants/apiResponse.constant'
import { showMessageError } from 'utils/storeHube/helpers'
import useQuery from 'utils/hooks/useQuery'
import { getTicketById } from 'views/tickets/store/dataSlice'

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']
const VIDEO_EXT = ['mp4', 'webm', 'mov', 'm4v', 'ogv']

const matchExt = (file, list) => {
    if (!file?.file) return false
    const ext = (file.extension || '').toLowerCase()
    if (ext) return list.includes(ext)
    // Fallback: deducir por extension del URL si no viene en el modelo.
    const fromUrl = file.file.split('?')[0].split('.').pop()?.toLowerCase()
    return list.includes(fromUrl)
}

export const isImageAttachment = (file) => matchExt(file, IMAGE_EXT)
export const isVideoAttachment = (file) => matchExt(file, VIDEO_EXT)
export const isPreviewableAttachment = (file) => isImageAttachment(file) || isVideoAttachment(file)

const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return null
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const footerIconBtnClass =
    'inline-flex items-center justify-center w-9 h-9 rounded-md text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'

const MIN_SCALE = 1
const MAX_SCALE = 6
const DOUBLE_CLICK_SCALE = 2.5
const WHEEL_INTENSITY = 0.0015

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// El ángulo que llega del backend no está normalizado: los cálculos que
// dependen de si la imagen está de costado comparan contra 90 y 270, y un -90
// o un 450 los haría fallar sin que se note.
const normalizeAngle = (value) => ((((Number(value) || 0) % 360) + 360) % 360)

/**
 * Zoom y desplazamiento del visor.
 *
 * El zoom vive en un wrapper aparte (`translate` + `scale`) y la rotación se
 * queda en el `<img>`. Componer las dos en un mismo `transform` obliga a
 * reescribir el desplazamiento en el eje rotado cada vez que se gira; con dos
 * capas, cada transformación es independiente.
 */
const useImageZoom = ({ rotation, enabled }) => {
    // Escala y desplazamiento van en un solo estado: el desplazamiento válido
    // depende de la escala, y separarlos obliga a anidar un `set` dentro del
    // updater del otro, que en StrictMode corre dos veces.
    const [transform, setTransform] = useState({ scale: MIN_SCALE, x: 0, y: 0 })
    const [isPanning, setIsPanning] = useState(false)
    const { scale } = transform

    // El viewport va en estado y no en un ref: el Dialog monta su contenido en
    // un commit posterior al que abre el modal, así que un ref todavía vale
    // null cuando corre el efecto que registra la rueda, y las dependencias no
    // vuelven a cambiar. Con estado, aparecer el nodo dispara el efecto.
    const [viewport, setViewport] = useState(null)
    const contentRef = useRef(null)

    const pointersRef = useRef(new Map())
    const pinchRef = useRef(null)
    const panRef = useRef(null)

    const reset = useCallback(() => {
        setTransform({ scale: MIN_SCALE, x: 0, y: 0 })
        pointersRef.current.clear()
        pinchRef.current = null
        panRef.current = null
        setIsPanning(false)
    }, [])

    // Con la imagen girada 90 o 270 el ancho visible es el alto del layout.
    const getContentSize = useCallback(() => {
        const node = contentRef.current
        if (!node) return { width: 0, height: 0 }
        const swapped = rotation === 90 || rotation === 270
        return {
            width: swapped ? node.offsetHeight : node.offsetWidth,
            height: swapped ? node.offsetWidth : node.offsetHeight,
        }
    }, [rotation])

    // Sin límite, arrastrar saca la imagen de la pantalla y no hay forma de
    // recuperarla salvo resetear. El margen permitido es lo que sobra del
    // contenido escalado respecto del viewport.
    const clampOffset = useCallback(
        (next, nextScale) => {
            if (!viewport) return next
            const { width, height } = getContentSize()
            const maxX = Math.max(0, (width * nextScale - viewport.clientWidth) / 2)
            const maxY = Math.max(0, (height * nextScale - viewport.clientHeight) / 2)
            return {
                x: clamp(next.x, -maxX, maxX),
                y: clamp(next.y, -maxY, maxY),
            }
        },
        [viewport, getContentSize]
    )

    /**
     * Escala manteniendo fijo el punto de la imagen que está bajo el cursor.
     * `origin` viene en coordenadas de pantalla; si falta, se usa el centro.
     *
     * `escalaPedida` acepta una función `(escalaActual) => nueva`. Es la forma
     * que tienen que usar los gestos continuos: el navegador despacha varios
     * `wheel` dentro de la misma tarea, y una escala calculada afuera del
     * updater sale del render viejo, así que los eventos de una ráfaga se
     * pisan entre sí en vez de componerse.
     */
    const zoomTo = useCallback(
        (escalaPedida, origin) => {
            if (!viewport) return

            setTransform((current) => {
                const pedida =
                    typeof escalaPedida === 'function' ? escalaPedida(current.scale) : escalaPedida
                const nextScale = clamp(pedida, MIN_SCALE, MAX_SCALE)

                if (nextScale === current.scale) return current
                if (nextScale === MIN_SCALE) return { scale: MIN_SCALE, x: 0, y: 0 }

                const rect = viewport.getBoundingClientRect()
                const point = origin
                    ? {
                          x: origin.x - rect.left - rect.width / 2,
                          y: origin.y - rect.top - rect.height / 2,
                      }
                    : { x: 0, y: 0 }

                const ratio = nextScale / current.scale
                const next = clampOffset(
                    {
                        x: point.x - (point.x - current.x) * ratio,
                        y: point.y - (point.y - current.y) * ratio,
                    },
                    nextScale
                )
                return { scale: nextScale, ...next }
            })
        },
        [viewport, clampOffset]
    )

    const zoomIn = useCallback(() => zoomTo((s) => s + 0.5), [zoomTo])
    const zoomOut = useCallback(() => zoomTo((s) => s - 0.5), [zoomTo])

    const handleDoubleClick = useCallback(
        (e) => {
            if (!enabled) return
            if (scale > MIN_SCALE) reset()
            else zoomTo(DOUBLE_CLICK_SCALE, { x: e.clientX, y: e.clientY })
        },
        [enabled, scale, reset, zoomTo]
    )

    // React adjunta `wheel` como listener pasivo, así que `preventDefault` no
    // corre y la página scrollea detrás del modal. Hay que registrarlo a mano.
    useEffect(() => {
        if (!viewport || !enabled) return

        const onWheel = (e) => {
            e.preventDefault()
            zoomTo((s) => s * Math.exp(-e.deltaY * WHEEL_INTENSITY), {
                x: e.clientX,
                y: e.clientY,
            })
        }

        viewport.addEventListener('wheel', onWheel, { passive: false })
        return () => viewport.removeEventListener('wheel', onWheel)
    }, [viewport, enabled, zoomTo])

    // El desplazamiento válido depende del tamaño del viewport y del de la
    // imagen, y los dos cambian solos: al redimensionar la ventana, al girar el
    // teléfono, y cuando la imagen termina de cargar y recién ahí tiene medidas.
    // Sin volver a acotar, un desplazamiento que era válido deja la imagen
    // despegada del borde hasta el gesto siguiente.
    useEffect(() => {
        if (!viewport || !enabled || typeof ResizeObserver === 'undefined') return

        const observer = new ResizeObserver(() => {
            setTransform((current) => {
                if (current.scale === MIN_SCALE) return current
                const next = clampOffset({ x: current.x, y: current.y }, current.scale)
                if (next.x === current.x && next.y === current.y) return current
                return { scale: current.scale, ...next }
            })
        })

        observer.observe(viewport)
        if (contentRef.current) observer.observe(contentRef.current)
        return () => observer.disconnect()
    }, [viewport, enabled, clampOffset])

    const getPinchState = () => {
        const points = Array.from(pointersRef.current.values())
        if (points.length < 2) return null
        const [a, b] = points
        return {
            distance: Math.hypot(a.x - b.x, a.y - b.y),
            center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
        }
    }

    // El desplazamiento se acumula por deltas y no desde un origen fijo: así
    // cualquier puntero puede tomar el gesto a mitad de camino sin arrastrar
    // el offset del render en que empezó.
    const startPan = (pointerId) => {
        if (!pointersRef.current.has(pointerId)) return
        panRef.current = { pointerId }
        setIsPanning(true)
    }

    // Sin capturar el puntero, cruzar el borde del visor durante el gesto
    // dispara `pointerleave` y lo corta a la mitad. Tira si el puntero ya no
    // está activo, que es justo cuando no hace falta capturarlo.
    const capturePointer = (node, pointerId) => {
        try {
            node?.setPointerCapture?.(pointerId)
        } catch (e) {
            /* el puntero terminó antes de que pudiéramos capturarlo */
        }
    }

    const onPointerDown = (e) => {
        if (!enabled) return
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

        if (pointersRef.current.size === 2) {
            const pinch = getPinchState()
            pinchRef.current = pinch ? { ...pinch, scale } : null
            pointersRef.current.forEach((_, id) => capturePointer(e.currentTarget, id))
            panRef.current = null
            setIsPanning(false)
            return
        }

        if (pointersRef.current.size === 1 && scale > MIN_SCALE) {
            capturePointer(e.currentTarget, e.pointerId)
            startPan(e.pointerId)
        }
    }

    const onPointerMove = (e) => {
        if (!enabled) return
        const previo = pointersRef.current.get(e.pointerId)
        if (!previo) return
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY })

        if (pinchRef.current) {
            const pinch = getPinchState()
            if (!pinch || !pinchRef.current.distance) return
            zoomTo(
                pinchRef.current.scale * (pinch.distance / pinchRef.current.distance),
                pinch.center
            )
            return
        }

        if (panRef.current?.pointerId !== e.pointerId) return
        const dx = e.clientX - previo.x
        const dy = e.clientY - previo.y
        setTransform((current) => ({
            scale: current.scale,
            ...clampOffset({ x: current.x + dx, y: current.y + dy }, current.scale),
        }))
    }

    const endPointer = (e) => {
        pointersRef.current.delete(e.pointerId)
        if (pointersRef.current.size < 2) pinchRef.current = null

        if (pointersRef.current.size === 0) {
            panRef.current = null
            setIsPanning(false)
            return
        }

        // Levantar un dedo del pinch y seguir corriendo la imagen con el otro
        // es el gesto natural en mobile, donde el pinch es la vía principal.
        // El dedo que queda nunca pasó por `onPointerDown` con un solo puntero,
        // así que hay que darle el desplazamiento acá.
        if (pointersRef.current.size === 1 && scale > MIN_SCALE) {
            const [restante] = pointersRef.current.keys()
            startPan(restante)
        }
    }

    return {
        scale,
        offset: { x: transform.x, y: transform.y },
        isPanning,
        isZoomed: scale > MIN_SCALE,
        canZoomIn: scale < MAX_SCALE,
        canZoomOut: scale > MIN_SCALE,
        reset,
        zoomIn,
        zoomOut,
        viewportRef: setViewport,
        contentRef,
        handlers: {
            onDoubleClick: handleDoubleClick,
            onPointerDown,
            onPointerMove,
            onPointerUp: endPointer,
            onPointerCancel: endPointer,
            onPointerLeave: endPointer,
        },
    }
}

const AttachmentPreviewDialog = ({ file, isOpen, onClose }) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const query = useQuery()
    const idTicket = query.get('ticket')

    // Rotación local del visor. Inicializa con el valor persistido en backend
    // (campo `rotate` que ya devuelve el modelo de attachment) y se resetea
    // cada vez que cambia la imagen abierta.
    const baseRotation = normalizeAngle(file?.rotate)
    const [rotation, setRotation] = useState(baseRotation)
    const [savingRotation, setSavingRotation] = useState(false)

    const zoomEnabled = isOpen && isImageAttachment(file)
    const zoom = useImageZoom({ rotation, enabled: zoomEnabled })

    const { reset: resetZoom } = zoom

    useEffect(() => {
        setRotation(normalizeAngle(file?.rotate))
        resetZoom()
    }, [file?.id, file?.file, resetZoom])

    // Girar con la imagen ampliada deja el encuadre en una zona que ya no
    // corresponde a lo que se estaba mirando.
    useEffect(() => {
        resetZoom()
    }, [rotation, resetZoom])

    if (!file) return null

    const ext = (file.extension || file.file?.split('.').pop() || '').toUpperCase()
    const size = formatBytes(file.size)
    const name = file.original_name || `archivo.${ext.toLowerCase() || 'file'}`
    const isVideo = isVideoAttachment(file)

    const rotateLeft = () => setRotation((r) => (r - 90 + 360) % 360)
    const rotateRight = () => setRotation((r) => (r + 90) % 360)
    const hasUnsavedRotation = rotation !== baseRotation

    const saveRotation = async () => {
        if (!file?.id || savingRotation) return
        setSavingRotation(true)
        try {
            const { status } = await apiRotateImage(file.id, { rotate: rotation })
            if (status === statusResponse.SUCCESS) {
                if (idTicket) dispatch(getTicketById(idTicket))
                toast.push(
                    <Notification type="success" duration={2500}>
                        {t('changes_saved', 'Cambios guardados')}
                    </Notification>
                )
            }
        } catch (e) {
            showMessageError(e?.response?.data?.errors)
        } finally {
            setSavingRotation(false)
        }
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            onRequestClose={onClose}
            width="min(960px, 95vw)"
            // Abajo de `sm` el modal va a pantalla completa. El alto lo resuelve
            // `.attachment-preview-dialog` en `_dialog.css`, que contempla la barra
            // de direcciones de mobile.
            className="attachment-preview-dialog"
            contentClassName="!p-0 overflow-hidden !rounded-none sm:!rounded-xl !m-0 sm:!mx-auto sm:!my-auto"
            overlayClassName="!flex !items-center !justify-center"
            closable={false}
        >
            <div className="flex flex-col bg-white dark:bg-gray-800 h-full sm:h-auto sm:max-h-[90vh] rounded-none sm:rounded-xl overflow-hidden">
                {/* Header — solo identidad del archivo + cerrar */}
                <div className="attachment-preview-header flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                            {name}
                        </p>
                        {(ext || size) && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate mt-0.5">
                                {[ext, size].filter(Boolean).join(' · ')}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
                        aria-label={t('close', 'Cerrar')}
                    >
                        <HiOutlineX className="w-5 h-5" />
                    </button>
                </div>

                {/* Body — imagen o video centrado */}
                <div
                    ref={isVideo ? undefined : zoom.viewportRef}
                    className={classNames(
                        // `min-h-0` para que el body pueda encogerse dentro del modal a
                        // pantalla completa: sin eso el mínimo automático del flex item
                        // lo estira al alto de la imagen y empuja la botonera afuera.
                        'flex items-center justify-center bg-gray-50 dark:bg-gray-900 flex-1 min-h-0 sm:min-h-[55vh] p-4',
                        isVideo ? 'overflow-auto' : 'overflow-hidden select-none',
                        !isVideo && (zoom.isPanning ? 'cursor-grabbing' : zoom.isZoomed && 'cursor-grab')
                    )}
                    // Sin `touch-action: none` el navegador se queda con el
                    // gesto de dos dedos y dispara `pointercancel` antes del
                    // primer `pointermove`: en touch no habría forma de entrar
                    // al zoom. Por eso no depende de que ya haya zoom aplicado.
                    style={zoomEnabled ? { touchAction: 'none' } : undefined}
                    {...(isVideo ? {} : zoom.handlers)}
                >
                    {isVideo ? (
                        <video
                            key={file.id || file.file}
                            src={file.file}
                            controls
                            preload="metadata"
                            controlsList="nodownload"
                            className="max-w-full max-h-full sm:max-h-[65vh] rounded shadow-sm bg-black"
                        >
                            {t('video_unsupported', 'Tu navegador no soporta este video.')}
                        </video>
                    ) : (
                        <div
                            // `h-full` le da al wrapper una altura concreta contra la
                            // que resolver el `max-h-full` de la imagen. Sin eso, a
                            // pantalla completa la imagen no tendría techo y taparía la
                            // botonera. La imagen sigue centrada, así que el centro del
                            // wrapper y el suyo coinciden y el origen del zoom no se
                            // corre.
                            className="h-full flex items-center justify-center"
                            style={{
                                transform: `translate(${zoom.offset.x}px, ${zoom.offset.y}px) scale(${zoom.scale})`,
                                // La cuenta que mantiene fijo el punto bajo el
                                // cursor asume que la escala sale del centro.
                                transformOrigin: 'center center',
                                transition: zoom.isPanning ? 'none' : 'transform 0.15s ease-out',
                            }}
                        >
                            <img
                                ref={zoom.contentRef}
                                src={file.file}
                                alt={name}
                                draggable={false}
                                className="max-w-full max-h-full sm:max-h-[65vh] object-contain rounded shadow-sm"
                                style={{
                                    transform: `rotate(${rotation}deg)`,
                                    transition: 'transform 0.2s ease',
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Footer — acciones */}
                <div className="attachment-preview-footer flex items-center justify-between gap-2 px-3 sm:px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
                    {/* Cluster izquierdo: zoom y rotación (solo para imágenes) */}
                    <div className="flex items-center gap-1">
                        {!isVideo && (
                            <>
                                {/* En pantallas chicas el zoom se maneja con pinch y
                                    doble tap: los botones no entran junto al resto. */}
                                <Tooltip title={t('zoom_out', 'Alejar')}>
                                    <button
                                        type="button"
                                        onClick={zoom.zoomOut}
                                        disabled={!zoom.canZoomOut}
                                        className={classNames(footerIconBtnClass, 'hidden sm:inline-flex')}
                                        aria-label={t('zoom_out', 'Alejar')}
                                    >
                                        <HiOutlineZoomOut className="w-4 h-4" />
                                    </button>
                                </Tooltip>
                                <span className="hidden sm:inline-block w-11 text-center text-[11px] tabular-nums text-gray-500 dark:text-gray-400">
                                    {Math.round(zoom.scale * 100)}%
                                </span>
                                <Tooltip title={t('zoom_in', 'Acercar')}>
                                    <button
                                        type="button"
                                        onClick={zoom.zoomIn}
                                        disabled={!zoom.canZoomIn}
                                        className={classNames(footerIconBtnClass, 'hidden sm:inline-flex')}
                                        aria-label={t('zoom_in', 'Acercar')}
                                    >
                                        <HiOutlineZoomIn className="w-4 h-4" />
                                    </button>
                                </Tooltip>
                                {zoom.isZoomed && (
                                    <Tooltip title={t('zoom_fit', 'Ajustar a la pantalla')}>
                                        <button
                                            type="button"
                                            onClick={zoom.reset}
                                            className={footerIconBtnClass}
                                            aria-label={t('zoom_fit', 'Ajustar a la pantalla')}
                                        >
                                            <HiOutlineArrowsExpand className="w-4 h-4" />
                                        </button>
                                    </Tooltip>
                                )}
                                <span className="w-px h-5 bg-gray-200 dark:bg-gray-700 mx-1" />
                                <Tooltip title={t('rotate_left', 'Rotar a la izquierda')}>
                                    <button
                                        type="button"
                                        onClick={rotateLeft}
                                        className={footerIconBtnClass}
                                        aria-label={t('rotate_left', 'Rotar a la izquierda')}
                                    >
                                        <LuRotateCcw className="w-4 h-4" />
                                    </button>
                                </Tooltip>
                                <Tooltip title={t('rotate_right', 'Rotar a la derecha')}>
                                    <button
                                        type="button"
                                        onClick={rotateRight}
                                        className={footerIconBtnClass}
                                        aria-label={t('rotate_right', 'Rotar a la derecha')}
                                    >
                                        <LuRotateCw className="w-4 h-4" />
                                    </button>
                                </Tooltip>
                                {file.id && hasUnsavedRotation && (
                                    <button
                                        type="button"
                                        onClick={saveRotation}
                                        disabled={savingRotation}
                                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white text-[12.5px] font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed ml-1"
                                    >
                                        <HiOutlineCheck className="w-4 h-4" />
                                        <span className="hidden sm:inline">
                                            {t('save', 'Guardar')}
                                        </span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Cluster derecho: abrir externa + descargar */}
                    <div className="flex items-center gap-2">
                        <Tooltip title={t('open_in_new_tab', 'Abrir en pestaña nueva')}>
                            <a
                                href={file.file}
                                target="_blank"
                                rel="noreferrer"
                                className={footerIconBtnClass}
                            >
                                <HiOutlineExternalLink className="w-4 h-4" />
                            </a>
                        </Tooltip>
                        <a
                            href={file.file}
                            download={name}
                            target="_blank"
                            rel="noreferrer"
                            // El primario del tema (`theme.config.js`: indigo, nivel
                            // 600) con el mismo hover que arma `Button` en su variante
                            // `solid`. Antes era un indigo-500 suelto, un tono más
                            // claro que el de cualquier otro botón principal.
                            className="inline-flex items-center gap-1.5 px-3 sm:px-4 h-9 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white text-[12.5px] font-medium transition-colors"
                        >
                            <HiOutlineDownload className="w-4 h-4" />
                            <span>{t('download', 'Descargar')}</span>
                        </a>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default AttachmentPreviewDialog
